import { useEffect, useState } from 'react';
import Ayuda from '../ui/Ayuda';
import {
  estados as cargarEstados, municipios as cargarMunicipios,
  indice, reconocer,
} from './geografia';

/* =====================================================================
   EL SELECTOR DE MUNICIPIO
   =====================================================================
   VACIO NO ES AVERIA, y todo este componente se diseña alrededor de eso.

   `catalogo_municipio` nace vacía a propósito (migración 480): los 2 477
   municipios no se escriben de memoria, se cargan del archivo oficial, y
   eso es la capa 2. Mientras no se haya cargado, este componente **es el
   campo de texto de siempre**: se escribe a mano y se guarda igual.

   Un desplegable vacío sin explicación es una pantalla rota; un campo de
   texto es exactamente lo que había ayer. Por eso el servidor devuelve
   `cargado`, y por eso viaja: la pantalla no tiene que adivinar si la
   lista está vacía porque falta cargarla o porque el estado no tiene
   municipios — lo segundo no existe.

   =====================================================================
   DEPENDE DEL ESTADO, Y NO BORRA LO ESCRITO AL CAMBIARLO
   =====================================================================
   Cambiar el estado NO limpia el municipio por su cuenta. Quien corrige
   «Nueveo León» a «Nuevo León» no quiere que se le borre «Monterrey» de
   al lado; y si el municipio deja de valer para el estado nuevo, se dice
   con el texto delante en vez de vaciarlo en silencio.

   Igual que arriba: `onChange` entrega el NOMBRE, que es lo que ya se
   guarda hoy. Cablearlo no cambia ni una columna.
   ===================================================================== */
export default function SelectorMunicipio({
  estado = '',
  valor = '',
  onChange,
  id = 'municipio',
  etiqueta = 'Municipio',
  requerido = false,
  deshabilitado = false,
}) {
  /* =====================================================================
     UN SOLO ESTADO, Y LLEVA DENTRO PARA QUE ESTADO ES
     =====================================================================
     `para` no es contabilidad: es lo que hace que al cambiar de estado la
     lista del anterior NO se enseñe ni un fotograma. Con tres estados
     sueltos habría un instante en que `lista` es la de Jalisco y `estado`
     ya dice Sonora, y ese instante es un desplegable que ofrece
     municipios de otro estado.

     Y de paso quita la única llamada a `setState` que este componente
     hacía en el cuerpo del efecto: lo demás se DEDUCE al pintar, que es
     lo que el lint pide y, en este caso, además es más correcto.
     ===================================================================== */
  const [datos, setDatos] = useState({ para: '', lista: [], cargado: false });

  useEffect(() => {
    let vivo = true;
    if (!estado) return undefined;
    cargarEstados()
      .then((filas) => reconocer(estado, indice(filas)))
      .then((clave) => (clave ? cargarMunicipios(clave)
        : { cargado: false, municipios: [] }))
      .then((r) => {
        if (vivo) {
          setDatos({ para: estado, lista: r.municipios ?? [],
                     cargado: Boolean(r.cargado) });
        }
      })
      .catch(() => {
        if (vivo) setDatos({ para: estado, lista: [], cargado: false });
      });
    return () => { vivo = false; };
  }, [estado]);

  const alDia = Boolean(estado) && datos.para === estado;
  const lista = alDia ? datos.lista : [];
  const cargado = alDia && datos.cargado;
  const pidiendo = Boolean(estado) && !alDia;

  const etiquetaCompleta = (
    <label htmlFor={id} className="block text-sm font-medium mb-1">
      {etiqueta}{requerido && <span className="text-red-600"> *</span>}
    </label>
  );

  /* =====================================================================
     SIN CATALOGO CARGADO: EL CAMPO DE TEXTO DE SIEMPRE
     ===================================================================== */
  if (!cargado) {
    return (
      <div>
        {etiquetaCompleta}
        <input
          id={id}
          type="text"
          value={valor}
          disabled={deshabilitado}
          onChange={(e) => onChange?.(e.target.value, null)}
          className="w-full rounded-lg border px-3 py-2 text-base"
          placeholder="Escribe el municipio"
        />
        <Ayuda id="importador.municipio_sin_catalogo" />
        {/* INDICADOR DE CARGA en cuanto pasa de un instante: la doctrina
            pide que ninguna pantalla se quede quieta sin decir por qué. */}
        {pidiendo && (
          <p className="mt-1 text-xs text-gray-500">Buscando la lista…</p>
        )}
      </div>
    );
  }

  const enLista = lista.some((m) => m.nombre === valor);

  return (
    <div>
      {etiquetaCompleta}
      <select
        id={id}
        value={enLista ? valor : ''}
        disabled={deshabilitado}
        onChange={(e) => {
          const elegido = lista.find((m) => m.nombre === e.target.value);
          onChange?.(e.target.value, elegido ?? null);
        }}
        className="w-full rounded-lg border px-3 py-2 text-base min-h-11"
      >
        <option value="">— Elige el municipio —</option>
        {lista.map((m) => (
          <option key={m.clave} value={m.nombre}>{m.nombre}</option>
        ))}
      </select>
      {valor && !enLista && (
        <p className="mt-1 text-xs text-amber-700">
          «{valor}» no está entre los municipios de este estado. Se conserva
          tal cual hasta que elijas uno de la lista.
        </p>
      )}
    </div>
  );
}
